import { useState, useMemo, useCallback, useEffect } from 'react';
import {
    MdOutlineCircle,
    MdAttachMoney,
    MdCircle,
    MdOutlinePhone,
    MdCheckCircle,
    MdCheckCircleOutline,
} from 'react-icons/md';
import { GiReceiveMoney } from 'react-icons/gi';
import { BsAsterisk } from 'react-icons/bs';
import { RiDeleteBinLine } from 'react-icons/ri';
import { RxCross2 } from 'react-icons/rx';
import { MdOutlineEmail } from 'react-icons/md';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import TeamDrawer from '../drawers/TeamDrawer';
import ClientInfoDrawer from '../drawers/ClientInfoDrawer';
import DeleteMemberModal from '../DeleteMember';
import { format } from 'date-fns';
import { createTeamMemberAccount, deleteTeamMember, toggleTeamMemberActive } from '~/services/campaign';
import { resetUserPassword } from '~/services/users';
import toast from 'react-hot-toast';

interface Props {
    campaign: any;
    metadata: any;
    csc?: any;
    patient?: any;
    application?: any;
    onDataUpdate?: () => void;
    campaignDispatch?: any;
}

interface TeamTab {
    id: number;
    access: string;
    campaign_ref: number;
    additional_phone: string;
    is_active: boolean;
    is_primary_contact: boolean;
    is_emergency_contact: boolean;
    can_discuss_balance: boolean;
    can_request_disbursement: boolean;
    update_date: string;
    permissions: string[];
    relationship_to_patient: {
        id: number;
        is_active: boolean;
        name: string;
        sequence: number;
    };
    role: string;
    person: {
        first_name: string;
        last_name: string;
        email?: string;
        last_login?: string;
        user?: number | null;
    };
    pending_password_reset?: {
        created_at: string;
        is_account_setup: boolean;
    } | null;
}

// Build permissions array from boolean flags
const buildPermissions = (member: TeamTab): string[] => {
    const perms: string[] = [];
    
    // Add access level permission - normalize the value
    if (member.access) {
        // Normalize access values like 'full team member' to 'full', 'limited team member' to 'limited'
        const normalizedAccess = member.access.toLowerCase().trim();
        if (normalizedAccess.includes('full')) {
            perms.push('full');
        } else if (normalizedAccess.includes('limited')) {
            perms.push('limited');
        } else if (normalizedAccess.includes('no')) {
            perms.push('no');
        } else {
            // If it doesn't match any pattern, use the original value
            perms.push(member.access);
        }
    }
    
    // Add flag-based permissions
    if (member.is_primary_contact) {
        perms.push('primary');
    }
    if (member.is_emergency_contact) {
        perms.push('emergency_contact');
    }
    if (member.can_discuss_balance) {
        perms.push('funds_inquiry');
    }
    if (member.can_request_disbursement) {
        perms.push('funds_request');
    }
    
    return perms;
};

const get_permission_icons = (perms: string[]) => {
    const icons = perms.map((item: string, i: number) => {
        let icon;

        switch (item) {
            case 'full':
                icon = <MdCircle className='text-primary' size={18} />;
                break;
            case 'limited':
                icon = (
                    <MdOutlineCircle className='text-warning' size={18} />
                );
                break;
            case 'no':
            case 'no_access':
                icon = <MdCircle className='text-danger' size={18} />;
                break;
            case 'primary':
                icon = <MdOutlinePhone size={18} />;
                break;
            case 'funds_inquiry':
                icon = <MdAttachMoney className='#afc12d' size={18} />;
                break;
            case 'funds_request':
                icon = <GiReceiveMoney size={18} />;
                break;
            case 'emergency_contact':
                icon = <BsAsterisk color='#952e46' size={18} />;
                break;
            default:
                // Don't render anything for unknown permission types
                return null;
        }

        return (
            <span key={i} className='me-2'>
                {icon}
            </span>
        );
    }).filter(Boolean); // Filter out null values

    return <>{icons}</>;
};

const TeamTab: React.FC<Props> = ({ campaign, metadata, csc, patient, application, onDataUpdate, campaignDispatch }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isClientDrawerOpen, setIsClientDrawerOpen] = useState(false);
    const [add, setAdd] = useState(true);
    const [teamMember, setTeamMember] = useState<TeamTab | undefined>(
        undefined
    );

    const columnHelper = createColumnHelper<TeamTab>();
    
    const handleDeleteTeamMember = useCallback(async (id: number) => {
        try {
            await deleteTeamMember(id, campaign?.campaign_id);
            toast.success('Team member deleted successfully');
            
            // Update team members in context instead of refetching
            if (campaignDispatch) {
                campaignDispatch({
                    type: 'remove_team_member',
                    payload: { id },
                });
            } else if (onDataUpdate) {
                // Fallback to full refresh if campaignDispatch not provided
                onDataUpdate();
            }
        } catch (error) {
            console.error('Error deleting team member:', error);
            toast.error('Failed to delete team member');
        }
    }, [campaign?.campaign_id, onDataUpdate, campaignDispatch]);

    const handleToggleActive = useCallback(async (id: number, currentIsActive: boolean) => {
        const campaignId = campaign?.campaign_id || campaign?.id;
        if (!campaignId) return;
        
        try {
            const resp = await toggleTeamMemberActive(id, campaignId, !currentIsActive);
            
            if (resp.detail) {
                toast.error(resp.detail);
                return;
            }
            
            toast.success(`Team member ${!currentIsActive ? 'activated' : 'deactivated'} successfully`);
            
            // Update team member in context instead of refetching
            if (campaignDispatch) {
                campaignDispatch({
                    type: 'update_team_member',
                    payload: resp,
                });
            } else if (onDataUpdate) {
                // Fallback to full refresh if campaignDispatch not provided
                onDataUpdate();
            }
        } catch (error) {
            console.error('Error toggling team member active status:', error);
            toast.error('Failed to update team member status');
        }
    }, [campaign?.campaign_id, campaign?.id, onDataUpdate, campaignDispatch]);

    // Resend password reset modal
    const [resendModal, setResendModal] = useState<{ userId: number; name: string; isAccountSetup: boolean } | null>(null);
    const [isResending, setIsResending] = useState(false);

    const handleResendPasswordReset = useCallback(async () => {
        if (!resendModal) return;
        setIsResending(true);
        try {
            await resetUserPassword(resendModal.userId);
            const emailLabel = resendModal.isAccountSetup ? 'User profile setup' : 'Password reset';
            toast.success(`${emailLabel} email sent to ${resendModal.name}`);
            setResendModal(null);
            onDataUpdate?.();
        } catch (error) {
            console.error('Error resending password reset:', error);
            toast.error('Failed to send email');
        } finally {
            setIsResending(false);
        }
    }, [resendModal, onDataUpdate]);

    // Create account state
    const [creatingAccountForId, setCreatingAccountForId] = useState<number | null>(null);

    const handleCreateAccount = useCallback(async (teamMemberId: number, name: string) => {
        setCreatingAccountForId(teamMemberId);
        try {
            const resp = await createTeamMemberAccount(teamMemberId);
            toast.success(`User profile created and setup email sent to ${name}`);
            if (campaignDispatch) {
                campaignDispatch({
                    type: 'update_team_member',
                    payload: resp,
                });
            } else if (onDataUpdate) {
                onDataUpdate();
            }
        } catch (error: any) {
            console.error('Error creating account:', error);
            toast.error(error.message || 'Failed to create user profile');
        } finally {
            setCreatingAccountForId(null);
        }
    }, [campaignDispatch, onDataUpdate]);

    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27 && !isResending) setResendModal(null);
        };
        document.addEventListener('keydown', keyHandler);
        return () => document.removeEventListener('keydown', keyHandler);
    }, [isResending]);

    // Create synthetic team members for CSC and patient
    const syntheticMembers: TeamTab[] = useMemo(() => {
        const members: TeamTab[] = [];
        
        if (csc) {
            members.push({
                id: -1, // Use negative ID to identify as synthetic
                access: 'full',
                campaign_ref: campaign?.id || 0,
                additional_phone: '',
                is_active: true,
                is_primary_contact: false,
                is_emergency_contact: false,
                can_discuss_balance: true,
                can_request_disbursement: true,
                update_date: '',
                permissions: ['full'],
                relationship_to_patient: {
                    id: 0,
                    is_active: true,
                    name: 'Patient Services Coordinator',
                    sequence: 0,
                },
                role: 'CSC',
                person: {
                    first_name: csc.first_name || '',
                    last_name: csc.last_name || '',
                },
            });
        }
        
        if (patient?.person) {
            members.push({
                id: -2, // Use negative ID to identify as synthetic
                access: 'full',
                campaign_ref: campaign?.id || 0,
                additional_phone: '',
                is_active: true,
                is_primary_contact: false,
                is_emergency_contact: false,
                can_discuss_balance: true,
                can_request_disbursement: true,
                update_date: patient.person.update_date || '',
                permissions: ['full'],
                relationship_to_patient: {
                    id: 0,
                    is_active: true,
                    name: 'Client',
                    sequence: 0,
                },
                role: 'Patient',
                person: {
                    first_name: patient.person.first_name || '',
                    last_name: patient.person.last_name || '',
                    email: patient.person.email || '',
                    last_login: patient.person.last_login || '',
                    user: patient.person.user || null,
                },
                pending_password_reset: patient.pending_password_reset || null,
            });
        }
        
        return members;
    }, [csc, patient, campaign?.id]);

    const columns = useMemo(() => [
        columnHelper.accessor((row) => row.person.last_login, {
            id: 'last_login',
            header: 'Last Login Date',
            cell: (info) => {
                const value = info.renderValue();
                if (!value) return '';
                // Hide Unix epoch dates (1970-01-01) which indicate user never logged in
                const date = new Date(value);
                if (date.getFullYear() <= 1970) return '';
                return format(date, 'MM/dd/yyyy');
            },
        }),
        columnHelper.accessor(
            (row) =>
                row.person
                    ? `${row.person.first_name} ${row.person.last_name}`
                    : 'N/A',
            {
                id: 'name',
                header: () => 'Name',
                cell: (info) => {
                    const rowId = info.row.original.id;
                    const isClient = rowId === -2;
                    const isCsc = rowId === -1;
                    const isClickable = !isCsc; // CSC is not clickable, but Client and regular team members are
                    
                    return (
                        <span 
                            className={`font-bold text-primary ${isClickable ? 'cursor-pointer hover:underline' : ''}`}
                            onClick={() => {
                                if (isClient) {
                                    // Open client info drawer for the Client row
                                    setIsClientDrawerOpen(true);
                                } else if (!isCsc) {
                                    // Open team drawer for regular team members
                                    setTeamMember(info.row.original);
                                    setAdd(false);
                                    setIsDrawerOpen(true);
                                }
                            }}
                        >
                            {info.renderValue()}
                        </span>
                    );
                },
            }
        ),
        columnHelper.accessor('relationship_to_patient.name', {
            header: 'Relationship',
            cell: (info) => info.renderValue(),
        }),
        columnHelper.accessor('permissions', {
            header: 'Permissions',
            cell: (info) => {
                const isSynthetic = info.row.original.id < 0;
                // For synthetic members (CSC and Patient), only show full access icon
                if (isSynthetic) {
                    return get_permission_icons(['full']);
                }
                // Build permissions from boolean flags for regular team members
                const perms = buildPermissions(info.row.original);
                return get_permission_icons(perms);
            },
        }),
        columnHelper.accessor('pending_password_reset', {
            id: 'password_status',
            header: 'Password Status',
            cell: (info) => {
                const member = info.row.original;
                // CSC is an admin user, not a portal user
                if (member.id === -1) return '—';

                const token = info.getValue();
                const userId = member.person?.user;
                const name = `${member.person.first_name} ${member.person.last_name}`;

                // No pending token — show last login as "password set" date
                if (!token) {
                    const lastLogin = member.person?.last_login;
                    if (!lastLogin || new Date(lastLogin).getFullYear() <= 1970) {
                        if (userId) {
                            return (
                                <span className='text-[11px] text-gray-400'>
                                    Never logged in
                                </span>
                            );
                        }
                        const email = member.person?.email;
                        return (
                            <div className='flex flex-col gap-0.5'>
                                <span className='text-[11px] text-gray-400'>
                                    No user profile
                                </span>
                                {email && member.id > 0 && (
                                    <button
                                        onClick={() => handleCreateAccount(member.id, name)}
                                        disabled={creatingAccountForId === member.id}
                                        className='flex w-fit items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50'
                                    >
                                        <MdOutlineEmail size={14} />
                                        {creatingAccountForId === member.id ? 'Creating...' : 'Create User Profile'}
                                    </button>
                                )}
                            </div>
                        );
                    }
                    const date = new Date(lastLogin);
                    return (
                        <div className='flex flex-col gap-0.5'>
                            <span className='text-sm'>
                                {format(date, 'MM/dd/yyyy')}
                            </span>
                            <span className='text-[11px] text-green-700'>
                                Password set
                            </span>
                        </div>
                    );
                }

                const hasEmail = !!member.person?.email;

                return (
                    <div className='flex flex-col gap-0.5'>
                        <span className='text-sm'>
                            {format(new Date(token.created_at), 'MM/dd/yyyy')}
                        </span>
                        <span className={`text-[11px] ${
                            !hasEmail
                                ? 'text-gray-400'
                                : token.is_account_setup
                                    ? 'text-blue-700'
                                    : 'text-amber-700'
                        }`}>
                            {!hasEmail
                                ? (token.is_account_setup
                                    ? 'User profile setup pending — no email on file'
                                    : 'Password reset pending — no email on file')
                                : (token.is_account_setup ? 'User profile setup sent' : 'Password reset sent')}
                        </span>
                        {userId && hasEmail && (
                            <button
                                onClick={() => setResendModal({ userId, name, isAccountSetup: !!token.is_account_setup })}
                                className='flex w-fit items-center gap-1 text-xs text-primary hover:underline'
                            >
                                <MdOutlineEmail size={14} />
                                Resend
                            </button>
                        )}
                    </div>
                );
            },
        }),
        columnHelper.accessor('id', {
            id: 'actions',
            header: ' ',
            enableSorting: false,
            cell: (info) => {
                const isSynthetic = info.row.original.id < 0;
                const isActive = info.row.original.is_active;
                return isSynthetic ? (
                    <div className='flex items-center gap-2'>
                        <button
                            disabled
                            className='cursor-not-allowed opacity-30'
                            title='Cannot modify this member'
                        >
                            <MdCheckCircle className='text-primary' size={18} />
                        </button>
                        <button
                            disabled
                            className='cursor-not-allowed opacity-30'
                            title='Cannot delete this member'
                        >
                            <RiDeleteBinLine className='text-red' size={18} />
                        </button>
                    </div>
                ) : (
                    <div className='flex items-center gap-2'>
                        <button
                            onClick={() => handleToggleActive(info.row.original.id, isActive)}
                            title={isActive ? 'Click to deactivate' : 'Click to activate'}
                            className='transition-colors hover:opacity-70'
                        >
                            {isActive ? (
                                <MdCheckCircle className='text-primary' size={18} />
                            ) : (
                                <MdCheckCircleOutline className='text-meta-4' size={18} />
                            )}
                        </button>
                        <DeleteMemberModal 
                            id={info.row.original.id} 
                            onDelete={handleDeleteTeamMember}
                        />
                    </div>
                );
            },
        }),
    ], [campaign?.campaign_id, handleDeleteTeamMember, handleToggleActive]);

    // Combine synthetic members with regular team members
    const allMembers = useMemo(() => [
        ...syntheticMembers,
        ...(campaign?.team_members || []),
    ], [syntheticMembers, campaign?.team_members]);

    const table = useReactTable({
        data: allMembers,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <div>
                <div className='flex justify-between'>
                    <h2 className='text-[22px]/[28px] font-bold'>Team</h2>
                    <div>
                        <button
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            onClick={() => {
                                setAdd(true);
                                setTeamMember(undefined);
                                setIsDrawerOpen(true);
                            }}
                        >
                            Add Team Member
                        </button>
                    </div>
                </div>
                <div className='flex flex-wrap justify-end gap-x-5 gap-y-3 py-4 text-sm'>
                    <div className='flex items-center'>
                        <span className='me-3'>
                            <MdCircle className='text-primary' />
                        </span>
                        Full Access
                    </div>
                    <div className='flex items-center'>
                        <span className='me-3'>
                            <MdOutlineCircle className='text-warning' />
                        </span>
                        Limited Access
                    </div>
                    <div className='flex items-center'>
                        <span className='me-3'>
                            <MdCircle className='text-danger' />
                        </span>
                        No Access
                    </div>
                    <div className='flex items-center'>
                        <span className='me-3'>
                            <MdOutlinePhone />
                        </span>
                        Primary Contact
                    </div>
                    <div className='flex items-center'>
                        <span className='me-3'>
                            <MdAttachMoney color='#afc12d' />
                        </span>
                        May inquire about funds raised or disbursed
                    </div>
                    <div className='flex items-center'>
                        <span className='me-3'>
                            <GiReceiveMoney />
                        </span>
                        May submit Fund Requests on behalf of the client
                    </div>
                    <div className='flex items-center'>
                        <span className='me-3'>
                            <BsAsterisk color='#952e46' />
                        </span>
                        Emergency Contact
                    </div>
                    <div className='flex items-center'>
                        <span className='me-3'>
                            <MdCheckCircle className='text-success' />
                        </span>
                        Active
                    </div>
                    <div className='flex items-center'>
                        <span className='me-3'>
                            <MdCheckCircleOutline className='text-meta-4' />
                        </span>
                        Inactive
                    </div>
                    <div className='flex items-center'>
                        <span className='me-3'>
                            <RiDeleteBinLine className='text-red' />
                        </span>
                        Delete
                    </div>
                </div>
            </div>

            <table className='datatable-table datatable-one w-full table-auto border-collapse overflow-hidden break-words px-4 md:table-fixed md:overflow-auto md:px-8'>
                <thead className='border-separate px-4'>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr
                            className='border-t border-stroke bg-whiten'
                            style={{ fontSize: '14px' }}
                            key={headerGroup.id}
                        >
                            {headerGroup.headers.map((header) => (
                                <th
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                        ...(header.column.columnDef.id ===
                                            'actions' && { width: '80px' }),
                                    }}
                                    key={header.id}
                                >
                                    <div className='flex items-center'>
                                        <span>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef
                                                          .header,
                                                      header.getContext()
                                                  )}
                                        </span>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr className='border-t border-stroke' key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                                <td
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                    }}
                                    key={cell.id}
                                >
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>

            <TeamDrawer
                campaign={campaign}
                isOpen={isDrawerOpen}
                setIsOpen={setIsDrawerOpen}
                isAdd={add}
                teamMember={teamMember}
                metadata={metadata}
                onDataUpdate={onDataUpdate}
                campaignDispatch={campaignDispatch}
            />

            <ClientInfoDrawer
                campaign={campaign}
                isOpen={isClientDrawerOpen}
                setIsOpen={setIsClientDrawerOpen}
                person={patient?.person}
                patient={patient}
                application={application}
                states={metadata?.states}
                maritalStatuses={metadata?.marital_statuses}
                preferredLanguages={metadata?.preferred_languages}
                ethnicities={metadata?.ethnicities}
                onDataSaved={onDataUpdate}
            />

            {resendModal && (
                <div
                    className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75'
                    onClick={!isResending ? () => setResendModal(null) : undefined}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
                    >
                        <button
                            className='text-gray-600 hover:text-gray-900 absolute right-4 top-4 disabled:cursor-not-allowed disabled:opacity-50'
                            onClick={() => setResendModal(null)}
                            disabled={isResending}
                        >
                            <RxCross2 />
                        </button>
                        <div className='p-8'>
                            <h2 className='mb-[30px] text-[22px] font-bold'>
                                {resendModal.isAccountSetup ? 'Resend User Profile Setup Email' : 'Resend Password Email'}
                            </h2>
                            <p className='mb-[30px]'>
                                Are you sure you want to resend the{' '}
                                {resendModal.isAccountSetup ? 'user profile setup' : 'password reset'} email to{' '}
                                <strong>{resendModal.name}</strong>?
                            </p>
                            <div className='flex justify-start space-x-4'>
                                <button
                                    onClick={() => setResendModal(null)}
                                    disabled={isResending}
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleResendPasswordReset}
                                    disabled={isResending}
                                    className='rounded-full bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
                                >
                                    {isResending ? 'Sending...' : 'Resend'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TeamTab;
